import classNames from 'classnames';
import {Moment} from 'moment';
import React from 'react';

import EntryActions from 'actions/EntryActions';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  CalendarEntriesByDate,
  CalendarFilesByDate,
  CalendarInteraction,
  CalendarItem,
  CalendarProcessTasksByDate,
  CalendarProjectsByDate,
  CalendarRequestsByDate,
  CalendarView as CalendarViewType,
  LayeredCalendarItems,
  ViewRange,
} from 'calendar/CalendarConstants';
import CreateEntryModal from 'folder-items/CreateEntryModal';
// @ts-expect-error
import {formatDate} from 'notebook/entries/EntryDayOps';
import CreateEntryFromTemplateModal from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import * as ModalContainer from 'ui-library/ModalContainer';
import PositionedPopoverClass from 'ui-library/PositionedPopover';
import {ElementMouseEvent} from 'util/Types';

import CalendarActions from './CalendarActions';
import CalendarDay from './CalendarDay';
import CalendarMonth from './CalendarMonth';
import CalendarUtil, {isDragCompleteInteraction} from './CalendarUtil';
import CalendarWeek from './CalendarWeek';
import CalendarWeekHeader from './CalendarWeekHeader';
import NewEntryPositionedPopover from './NewEntryPositionedPopover';

interface Props {
  currentView: CalendarViewType;
  entries: CalendarEntriesByDate;
  files: CalendarFilesByDate;
  requests: CalendarRequestsByDate;
  processTasks: CalendarProcessTasksByDate;
  interact: CalendarInteraction;
  isLoadingData: boolean;
  layeredItems: LayeredCalendarItems<CalendarItem>;
  projects: CalendarProjectsByDate;
  today: Moment;
  viewRange: ViewRange;
  visibleDate: Moment;
  showWeeklyFileActivity: boolean;
}

class CalendarView extends React.Component<Props> {
  static CLASS_NAME = 'calendarView';

  closeCreatePopover = (e: ElementMouseEvent): void => {
    if (!PositionedPopoverClass.mouseDownInPopover(e)) {
      CalendarActions.closeCreatePopover();
    }
  };

  renderCalendarContent = (viewStart: Moment, viewEnd: Moment): JSX.Element => {
    switch (this.props.currentView) {
      case CalendarViewType.MONTH_VIEW: {
        return (
          <CalendarMonth
            today={this.props.today}
            items={this.props.layeredItems}
            startDate={viewStart}
            endDate={viewEnd}
            monthStart={this.props.visibleDate.clone().startOf('month')}
            interact={this.props.interact}
          />
        );
      }
      case CalendarViewType.WEEK_VIEW: {
        const week1 = viewStart;
        const week2 = viewStart.clone().add(1, 'weeks');
        return (
          <div className="calendarBorderBottom">
            <CalendarWeekHeader />
            <CalendarWeek
              today={this.props.today}
              weekStart={week1}
              items={CalendarUtil.calendarItemsInWeek(this.props.layeredItems, week1)}
              files={this.props.files}
              interact={this.props.interact}
              showWeeklyFileActivity={this.props.showWeeklyFileActivity}
            />
            <CalendarWeek
              today={this.props.today}
              weekStart={week2}
              items={CalendarUtil.calendarItemsInWeek(this.props.layeredItems, week2)}
              files={this.props.files}
              interact={this.props.interact}
              showWeeklyFileActivity={this.props.showWeeklyFileActivity}
            />
          </div>
        );
      }
      default: {
        assert(
          this.props.currentView === CalendarViewType.DAY_VIEW,
          `Unexpected view: ${this.props.currentView}`
        );
        const dateKey = this.props.visibleDate.format('YYYY-MM-DD');
        return (
          <div className="calendarView-dayView-container">
            <CalendarDay
              date={this.props.visibleDate}
              isLoadingData={this.props.isLoadingData}
              projects={this.props.projects[dateKey] ?? []}
              entries={this.props.entries[dateKey] ?? []}
              files={this.props.files[dateKey] ?? []}
              requests={this.props.requests[dateKey] ?? []}
              processTasks={this.props.processTasks[dateKey] ?? []}
            />
          </div>
        );
      }
    }
  };

  render(): JSX.Element {
    const [viewStart, viewEnd] = this.props.viewRange;
    return (
      <div
        className={classNames('preview-card', CalendarView.CLASS_NAME)}
        onMouseDown={isDragCompleteInteraction(this.props.interact) ? this.closeCreatePopover : undefined}
      >
        {(() => {
          if (isDragCompleteInteraction(this.props.interact)) {
            const [startDate, endDate] = CalendarUtil.rangeFromInteract(this.props.interact);
            // When flags are enabled for the new entry schema UI, we should show those modals instead of
            // the popover
            if (BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL) {
              if (BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION) {
                // Show the create entry modal
                ModalContainer.open(
                  CreateEntryModal,
                  <CreateEntryModal
                    onRequestClose={() => {
                      ModalContainer.close(CreateEntryModal);
                    }}
                    fulfillments={[]}
                    initialDay={startDate}
                    numDays={endDate.diff(startDate, 'days')}
                  />
                );
              } else {
                // If blank entry creation is disabled, show create from template
                ModalContainer.open(
                  CreateEntryFromTemplateModal,
                  <CreateEntryFromTemplateModal
                    onRequestClose={() => {
                      ModalContainer.close(CreateEntryFromTemplateModal);
                    }}
                    onRequestSubmit={(templateArgs) => {
                      EntryActions.createFromTemplate({
                        ...templateArgs,
                        initialDay: formatDate(startDate),
                        onSuccess: () => {
                          ModalContainer.close(CreateEntryFromTemplateModal);
                        },
                      });
                    }}
                    isTemplateSelectorDisabled={false}
                  />
                );
              }
            } else if (BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION) {
              // Only show the popover if blank entry creation is enabled
              return (
                <NewEntryPositionedPopover
                  left={this.props.interact.mouseUpCoords.x}
                  top={this.props.interact.mouseUpCoords.y}
                  startDate={startDate}
                  endDate={endDate}
                />
              );
            }
          }
          return null;
        })()}
        {this.renderCalendarContent(viewStart, viewEnd)}
      </div>
    );
  }
}

export default CalendarView;
